import classNames from 'classnames';
import { FunctionComponent, HTMLAttributes, useEffect, useState } from 'react';
import styled from 'styled-components';

import { PdfEmbedModes } from '@enums/blocks';
import { Block } from '@typings/block';
import { getBlockDataIsPdf } from '@utilities/block';

const appendScript = (src: string) => {
  return new Promise((resolve, reject) => {
    const script = document.querySelector('#adobe_dc_view_sdk');
    if (script) {
      return;
    } else {
      const s = document.createElement('script');
      s.setAttribute('id', 'adobe_dc_view_sdk');
      s.setAttribute('src', src);
      s.addEventListener('error', reject);
      s.addEventListener('load', resolve);
      document.body.append(s);
    }
  });
};

const createScript = async () => {
  await appendScript('https://acrobatservices.adobe.com/view-sdk/viewer.js');
};

const verifyScript = (): boolean => {
  if (typeof window === 'undefined') return false;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return typeof window.AdobeDC?.View === 'function';
};

interface PdfRenderProps extends HTMLAttributes<HTMLDivElement> {
  block: Block;
}

const UnstyledPdfRender: FunctionComponent<PdfRenderProps> = (props) => {
  const { block, ...otherProps } = props;
  const { key } = block;

  const data = getBlockDataIsPdf(block.data) ? block.data : null;

  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (data && !loaded) {
      createScript();

      const timer = setInterval(() => {
        if (verifyScript()) {
          setLoaded(true);
          clearInterval(timer);
        }
      }, 100);

      return () => clearInterval(timer);
    }
  }, [data, loaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loaded) {
      loadPdf();
    }
  }, [loaded]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return null;

  const { cdnUrl, embedMode = PdfEmbedModes.SizedContainer, height, name, width } = data;

  const loadPdf = () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const adobeDCView = new AdobeDC.View({
      clientId: '8d4ddb64117e4664a02dccdda2c127a8',
      divId: key
    });

    adobeDCView.previewFile(
      {
        content: {
          location: {
            url: cdnUrl
          }
        },
        metaData: {
          fileName: name
        }
      },
      {
        /** https://developer.adobe.com/document-services/docs/overview/pdf-embed-api/howtos/#embed-modes */
        embedMode
      }
    );
  };

  return (
    <div
      {...otherProps}
      className={classNames({
        pdf: true,
        [`${otherProps?.className}`]: !!otherProps?.className
      })}
      id={key}
      style={{
        aspectRatio: embedMode === PdfEmbedModes.SizedContainer ? `${width} / ${height}` : undefined
      }}
    />
  );
};

const StyledPdfRender = styled(UnstyledPdfRender)``;

/**
 * https://developer.adobe.com/document-services/docs/overview/pdf-embed-api/
 *
 * @param props PdfRenderProps
 * @returns FunctionComponent<PdfRenderProps>
 */
export const PdfRender: FunctionComponent<PdfRenderProps> = (props) => {
  return <StyledPdfRender {...props} />;
};
